import AddIcon from "@mui/icons-material/Add";
import React from "react";
import { useTranslation } from "react-i18next";
import "../file_explorer/FileDropzone.css";

interface VariantFileDropzoneProps {
  /** Chosen through the browse dialog. Dropped files go via `PiDropRegion`. */
  onFiles: (files: FileList) => void;
  /** Passed to the file input so the picker pre-filters. */
  accept: string;
  /**
   * "banner" is the list view's full-width strip; "card" fills a tile cell.
   * Same behaviour and copy either way — only the box differs.
   */
  variant?: "banner" | "card";
}

/**
 * Click-to-browse upload affordance leading the localizations.
 *
 * It wears the file explorer's drop zone — same classes, same neutral-until-
 * dragged border — because it does the same job one level down; only the copy
 * differs, and it is the copy that carries the promise: the language is
 * picked afterwards, not now.
 *
 * Dragging is handled by the surrounding `PiDropRegion`, so a file can be
 * dropped anywhere in the list rather than only onto this strip.
 */
export default function VariantFileDropzone(props: VariantFileDropzoneProps) {
  const { onFiles, accept, variant = "banner" } = props;
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) onFiles(e.target.files);
  };

  const classname = [
    "filedropzone-root",
    variant === "banner" && "filedropzone-banner",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classname}
      onClick={() => inputRef.current?.click()}
      data-testid="variant-dropzone"
    >
      <div className="filedropzone-content">
        <div>
          <div className="filedropzone-icon">
            <AddIcon />
          </div>
          <div className="filedropzone-title">
            {t("localize_ui.upload_title")}
          </div>
          <div className="filedropzone-subtitle">
            {t("localize_ui.upload_subtitle")}
          </div>
        </div>
      </div>

      <input
        className="filedropzone-hide-input"
        ref={inputRef}
        type="file"
        onChange={handleFileInput}
        accept={accept}
        data-testid="variant-dropzone-input"
      />
    </div>
  );
}
